<template>
    <v-row no-gutters
           class="draggable day-task-row pa-0 ma-0"
           draggable="true">
        <v-col cols="12" md="4" lg="3" xl="2"
               class="pa-0 pr-4 pt-2 time-column">
            <TableTimeCard :startTime="task.dateAndTime.startTime"
                           :endTime="task.dateAndTime.endTime"
                           :duration="task.dateAndTime.duration"
                           alternative-text="..."
                           alternative-icon="mdi-clock-edit-outline"
                           @click="editDateTime = true" />
        </v-col>
        <v-col cols="12" md="8" lg="3" xl="4">
            <v-textarea label="Задача"
                        class="mt-5 mt-sm-5 mt-md-0"
                        auto-grow
                        rows="1"
                        @update:modelValue="updateDayTaskOnNextTick"
                        v-model="task.taskText" />
        </v-col>

        <v-spacer />

        <v-col cols="2" md="1" lg="1" xl="1"
               class="d-flex align-top justify-center pt-3">
            <ResultButtonsPair v-model="task.isCompleted"
                               @update:modelValue="onUpdateResult($event)" />
        </v-col>

        <v-spacer />

        <v-col cols="10" md="11" lg="5" xl="5"
               class="d-flex justify-space-between">
            <v-textarea label="Результат"
                        :class="resultTextColor"
                        :disabled="task.isCompleted === null"
                        auto-grow
                        rows="1"
                        @update:modelValue="updateDayTaskOnNextTick"
                        v-model="task.resultText" />

            <div v-if="task.isCompleted === false"
                 class="ml-1 mt-1">
                <v-tooltip location="bottom"
                           text="Перенести задачу">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               color="error"
                               @click="openCopyDialog"
                               icon="">П</v-btn>
                    </template>
                </v-tooltip>
                <v-tooltip location="bottom"
                           text="Делегировать задачу">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               class="ml-1"
                               color="error"
                               @click="openDelegateDialog"
                               icon="">Д</v-btn>
                    </template>
                </v-tooltip>
            </div>
        </v-col>
    </v-row>

    <v-dialog v-model="showDelegateDialog"
              class="copy-dialog"
              transition="dialog-bottom-transition"
              width="600">
        <v-card class="text-primary">
            <v-card-title>
                Делегирование задачи
            </v-card-title>

            <v-card-text class="py-0 px-4">
                <v-btn @click="showCopyDialog = true"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Делегировать
                </v-btn>

                <v-btn v-for="group in userGroups" :key="group.guid"
                       @click="delegateToGroup(group)"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Делегировать в группу "{{group.name}}" [{{getMiniGuid(group.guid)}}] ({{group.membersCount}})
                </v-btn>
            </v-card-text>
            <v-card-actions class="pt-0 pb-4 px-4">
                <v-spacer />
                <v-btn color="secondary"
                       variant="elevated"
                       @click="showDelegateDialog = false">Отменить</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <SelectUserDialog v-if="showDelegateToUserDialog"
                      :users="selectedGroup.members"
                      title="Делегировать пользователю:"
                      @hide="showDelegateToUserDialog = false"
                      @select:user="delegateToUser($event)" />

    <v-dialog v-model="showCopyDialog"
              class="copy-dialog"
              transition="dialog-bottom-transition"
              width="600">
        <v-card class="text-primary">
            <v-card-title v-if="delegate">
                Делегирование задачи на дату
            </v-card-title>
            <v-card-title v-else>
                Перенос задачи на дату
            </v-card-title>

            <v-card-text class="py-0 px-4">
                <v-btn @click="copyDayTask(getNextDate(1))"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Перенести на завтра ({{getDate(getNextDate(1))}})
                </v-btn>

                <v-btn @click="copyDayTask(getNextDate(2))"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Перенести на послезавтра ({{getDate(getNextDate(2))}})
                </v-btn>

                <v-btn @click="copyDayTask(getNextDate(7))"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Перенести на неделю ({{getDate(getNextDate(7))}})
                </v-btn>

                <v-btn @click="copyDayTask(getNextDate(14))"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Перенести на 2 недели ({{getDate(getNextDate(14))}})
                </v-btn>

                <v-btn @click="copyDayTask(null)"
                       class="w-100 mb-2"
                       color="primary"
                       variant="outlined">
                    Перенести на другую дату
                </v-btn>
            </v-card-text>
            <v-card-actions class="pt-0 pb-4 px-4">
                <v-spacer />
                <v-btn color="secondary"
                       variant="elevated"
                       @click="showCopyDialog = false">Отменить</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <CreateDayTaskDialog v-if="showCreateDialog"
                         :text="newDayTaskText"
                         :date="newDayTaskDate"
                         @hide="showCreateDialog = false"
                         @create:dayTask="createTask($event)" />

    <DateTimeDialog v-if="editDateTime"
                    @hide="editDateTime = false"
                    @update:modelValue="onUpdateDateTime($event)"
                    v-model="task.dateAndTime" />
</template>

<script>
    import DateTimeDialog from "@/components/DateTimeDialog.vue";

    import TableTimeCard from "@/components/client/day_tasks/TableTimeCard.vue";
    import ResultButtonsPair from "@/components/client/day_tasks/ResultButtonsPair.vue";
    import CreateDayTaskDialog from "@/components/client/day_tasks/CreateDayTaskDialog.vue";

    import SelectUserDialog from "@/components/SelectUserDialog.vue";

    import _debounce from "lodash/debounce";
    import { mapState } from "vuex";

    export default {
        components: {
            DateTimeDialog,
            TableTimeCard,
            ResultButtonsPair,
            CreateDayTaskDialog,
            SelectUserDialog
        },

        props: {
            dayTask: Object
        },

        data() {
            return {
                task: this.dayTask,

                editDateTime: false,

                showDelegateDialog: false,
                delegate: false,
                selectedGroup: null,

                showDelegateToUserDialog: false,
                selectedUser: null,

                showCopyDialog: false,
                newDayTaskDate: null,
                newDayTaskText: null,

                showCreateDialog: false
            };
        },

        emits: ["upsertDayTask"],

        computed: {
            ...mapState([
                "currentLocale",
                "currentDateTime",
                "userGroups",
                "userName"
            ]),

            resultTextColor() {
                if (this.task.isCompleted === true) {
                    return "text-success";
                }
                if (this.task.isCompleted === false) {
                    return "text-error";
                }

                return "";
            }
        },

        methods: {
            getMiniGuid(guid) {
                return guid.substring(0, 8);
            },

            getDate(dateTime) {
                if (!dateTime) {
                    return "";
                }

                return dateTime.setLocale(this.currentLocale).toFormat("d MMMM, EEEE");
            },

            openCopyDialog() {
                this.showCopyDialog = true;
                this.delegate = false;
            },

            openDelegateDialog() {
                this.showDelegateDialog = true;
                this.delegate = true;
            },

            delegateToGroup(group) {
                if (group.members === null && group.membersCount > 0) {
                    this.$store.commit("startLoading");

                    this.$store.state.groupsService
                        .getGroupWithUsers(group.guid)
                        .then(response => {
                            group.members = response.data.members;
                        })
                        .catch(error => this.$store.commit("error", error.message))
                        .finally(() => this.$store.commit("stopLoading"));
                }

                this.showDelegateToUserDialog = true;
                this.selectedGroup = group;
            },

            delegateToUser(user) {
                this.showCopyDialog = true;
                this.selectedUser = user;
            },

            copyDayTask(date) {
                this.showCopyDialog = false;
                this.showDelegateDialog = false;
                this.showDelegateToUserDialog = false;

                this.newDayTaskDate = date;

                this.newDayTaskText = this.delegate && !this.selectedUser
                    ? "Делегировать: " + this.task.taskText
                    : this.task.taskText;

                this.showCreateDialog = true;
            },

            getNextDate(daysCount) {
                return this.currentDateTime.plus({ days: daysCount });
            },

            onUpdateDateTime(dateAndTime) {
                this.task.dateAndTime = dateAndTime;

                this.updateDayTask();
            },

            onUpdateResult(eventValue) {
                if (eventValue === true) {
                    this.task.resultText = this.task.taskText;
                } else {
                    this.task.resultText = "Не получилось ";
                }

                this.updateDayTask();
            },

            updateDayTaskOnNextTick() {
                this.$nextTick(() => this.updateDayTaskLater(this.task));
            },

            updateDayTask() {
                this.updateDayTaskLater(this.task);
            },

            updateDayTaskLater: _debounce(
                function (task) {
                    this.$emit("upsertDayTask", task);
                },
                2000),

            createTask(task) {
                if (!this.delegate || !this.selectedUser) {
                    this.$emit("upsertDayTask", task);
                    return;
                }

                const initiatorTask = { ...task };
                const executorTask = { ...task };

                initiatorTask.taskText = `[для ${this.selectedUser.userName}] ` + task.taskText;
                this.$emit("upsertDayTask", initiatorTask);

                executorTask.taskText = `[от ${this.userName}] ` + task.taskText;
                executorTask.dateAndTime.date = task.dateAndTime.date.toFormat("yyyy-MM-dd");
                this.$store.dispatch("delegateDayTask", {
                    task: executorTask,
                    userId: this.selectedUser.id
                });
            }
        }
    };
</script>

<style lang="scss">
    .day-task-row {
    }
</style>